import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header, Footer, CategoryIcon, MobileBottomNav } from '../components/Layout';
import { betsApi } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { 
  Trophy, Shirt, Landmark, Tv, Newspaper, Share2, Cloud, Sparkles,
  DollarSign, Clock, ArrowLeft, Globe, Lock, CheckCircle2
} from 'lucide-react';

const categories = [
  { id: 'sports', name: 'Sports', icon: Trophy, color: '#22C55E' },
  { id: 'fashion', name: 'Fashion', icon: Shirt, color: '#EC4899' },
  { id: 'politics', name: 'Politics', icon: Landmark, color: '#6366F1' },
  { id: 'entertainment', name: 'TV & Entertainment', icon: Tv, color: '#F59E0B' },
  { id: 'news', name: 'News', icon: Newspaper, color: '#3B82F6' },
  { id: 'social', name: 'Social Media', icon: Share2, color: '#8B5CF6' },
  { id: 'weather', name: 'Weather', icon: Cloud, color: '#06B6D4' },
  { id: 'wildcard', name: 'Wildcard', icon: Sparkles, color: '#FF3B30' }
];

const CreateBet = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  
  const [formData, setFormData] = useState({
    category: '',
    title: '',
    description: '',
    rules: '',
    option_a: '',
    option_b: '',
    stake_amount: 10,
    stake_type: 'qbean',
    deadline_hours: 24,
    visibility: 'public',
    odds_a: 2.0,
    odds_b: 2.0
  });

  const [betCreated, setBetCreated] = useState(null);

  const handleCategorySelect = (catId) => {
    setFormData({ ...formData, category: catId });
    setStep(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.category || !formData.title || !formData.rules) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const { data } = await betsApi.create(formData);
      setBetCreated(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create bet');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#1A1A1A] flex flex-col pb-20 md:pb-0">
      <Header />
      
      <main className="flex-1 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {betCreated ? (
          <div className="card-dark text-center py-8">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#28A745]/20 flex items-center justify-center">
              <CheckCircle2 className="h-10 w-10 text-[#28A745]" />
            </div>
            <h1 className="text-2xl font-black text-white mb-2" style={{ fontFamily: 'Outfit' }}>Your Bet Has Been Created!</h1>
            <div className="bg-[#2D2D2D] rounded-lg p-4 inline-block mb-4">
              <div className="text-[#D4AF37] font-black text-2xl">{betCreated.bet_number}</div>
            </div>
            <p className="text-gray-400 mb-6">We will review your bet within <span className="text-[#D4AF37] font-bold">1 to 30 minutes</span>. Once approved, it will appear on the public dashboard.</p>
            <div className="flex gap-3 justify-center">
              <Button onClick={() => navigate('/dashboard')} className="btn-primary">Go to Dashboard</Button>
              <Button onClick={() => { setBetCreated(null); setStep(1); setFormData({...formData, title: '', description: '', rules: ''}); }} className="btn-secondary">Create Another</Button>
            </div>
          </div>
        ) : (
        <>
        <button 
          onClick={() => step > 1 ? setStep(step - 1) : navigate('/dashboard')}
          className="flex items-center gap-2 text-gray-400 hover:text-white mb-6"
        >
          <ArrowLeft className="h-4 w-4" /> Back
        </button>

        <div className="card-dark">
          <h1 className="text-2xl font-black uppercase mb-6 text-white" style={{ fontFamily: 'Outfit' }} data-testid="create-bet-title">
            Create a <span className="text-[#D4AF37]">Bet</span>
          </h1>

          {/* Progress */}
          <div className="flex gap-2 mb-8">
            {[1, 2, 3].map((s) => (
              <div 
                key={s}
                className={`h-2 flex-1 rounded-full ${s <= step ? 'bg-[#D4AF37]' : 'bg-[#2D2D2D]'}`}
              />
            ))}
          </div>

          {/* Step 1: Category */}
          {step === 1 && (
            <div>
              <h2 className="font-bold text-lg mb-4 text-white">Select a Category</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {categories.map((cat) => {
                  const Icon = cat.icon;
                  return (
                    <button
                      key={cat.id}
                      onClick={() => handleCategorySelect(cat.id)}
                      className={`p-4 rounded-lg border-2 text-left transition-all ${
                        formData.category === cat.id ? 'bg-[#D4AF37] text-[#1A1A1A] border-[#D4AF37]' : 'bg-[#2D2D2D] text-white border-[#3D3D3D] hover:border-[#D4AF37]'
                      }`}
                      data-testid={`select-category-${cat.id}`}
                    >
                      <Icon className="h-6 w-6 mb-2" style={{ color: formData.category === cat.id ? '#1A1A1A' : cat.color }} />
                      <div className="font-semibold text-sm">{cat.name}</div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Step 2: Details */}
          {step === 2 && (
            <div>
              <h2 className="font-bold text-lg mb-4 text-white">Bet Details</h2>
              <div className="space-y-4">
                <div>
                  <Label className="font-semibold text-white">Bet Title *</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="e.g., Chiefs will beat Pirates this Saturday"
                    className="input-dark mt-1"
                    maxLength={100}
                    data-testid="bet-title-input"
                  />
                </div>

                <div>
                  <div className="flex justify-between items-center">
                    <Label className="font-semibold text-white">Description</Label>
                    <span className="text-xs text-gray-500">{formData.description.length}/500</span>
                  </div>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Add more details about your bet..."
                    className="input-dark mt-1 min-h-[100px]"
                    maxLength={500}
                    data-testid="bet-description-input"
                  />
                </div>

                <div>
                  <div className="flex justify-between items-center">
                    <Label className="font-semibold text-white">Create Your Rules *</Label>
                    <span className="text-xs text-gray-500">{formData.rules.length}/500</span>
                  </div>
                  <Textarea
                    value={formData.rules}
                    onChange={(e) => setFormData({ ...formData, rules: e.target.value })}
                    placeholder="Define the rules for this bet. How will the winner be determined? What proof is required?..."
                    className="input-dark mt-1 min-h-[120px]"
                    maxLength={500}
                    data-testid="bet-rules-input"
                  />
                </div>

                <Button 
                  onClick={() => setStep(3)}
                  className={`w-full transition-all ${
                    formData.title && formData.rules 
                      ? 'btn-primary' 
                      : 'bg-[#2D2D2D] text-gray-500 cursor-not-allowed border-[#3D3D3D]'
                  }`}
                  disabled={!formData.title || !formData.rules}
                >
                  Continue
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Stake & Submit */}
          {step === 3 && (
            <form onSubmit={handleSubmit}>
              <h2 className="font-bold text-lg mb-4 text-white">Set Your Stake</h2>
              
              <div className="space-y-4">
                {/* Stake Type */}
                <div>
                  <Label className="font-semibold text-white">Currency</Label>
                  <div className="flex gap-2 mt-2">
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, stake_type: 'qbean' })}
                      className={`flex-1 p-3 rounded-lg border-2 font-semibold ${
                        formData.stake_type === 'qbean' ? 'bg-[#D4AF37] text-[#1A1A1A] border-[#D4AF37]' : 'bg-[#2D2D2D] text-white border-[#3D3D3D]'
                      }`}
                      data-testid="stake-type-qbean"
                    >
                      <Sparkles className="h-5 w-5 inline mr-2" />
                      Qbean Tokens
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, stake_type: 'real' })}
                      className={`flex-1 p-3 rounded-lg border-2 font-semibold ${
                        formData.stake_type === 'real' ? 'bg-[#28A745] text-white border-[#28A745]' : 'bg-[#2D2D2D] text-white border-[#3D3D3D]'
                      }`}
                      data-testid="stake-type-real"
                    >
                      <DollarSign className="h-5 w-5 inline mr-2" />
                      Real Money
                    </button>
                  </div>
                </div>

                {/* Amount */}
                <div>
                  <Label className="font-semibold text-white">Stake Amount ($1 – max $10,000)</Label>
                  <div className="relative mt-1">
                    {formData.stake_type === 'real' && (
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-500" />
                    )}
                    <Input
                      type="number"
                      min="1"
                      max="10000"
                      value={formData.stake_amount}
                      onChange={(e) => setFormData({ ...formData, stake_amount: parseFloat(e.target.value) || 0 })}
                      className={`input-dark ${formData.stake_type === 'real' ? 'pl-10' : ''}`}
                      data-testid="stake-amount-input"
                    />
                    {formData.stake_type === 'qbean' && (
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 font-semibold">Q</span>
                    )}
                  </div>
                  <div className="flex gap-2 mt-2">
                    {[10, 50, 100, 500].map((amt) => (
                      <button
                        key={amt}
                        type="button"
                        onClick={() => setFormData({ ...formData, stake_amount: amt })}
                        className={`px-3 py-1 rounded border-2 text-sm font-semibold ${
                          formData.stake_amount === amt ? 'bg-[#D4AF37] text-[#1A1A1A] border-[#D4AF37]' : 'bg-[#2D2D2D] text-white border-[#3D3D3D]'
                        }`}
                      >
                        {formData.stake_type === 'real' ? '$' : ''}{amt}{formData.stake_type === 'qbean' ? ' QBC' : ''}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Deadline */}
                <div>
                  <Label className="font-semibold text-white">Bet Deadline</Label>
                  <div className="grid grid-cols-4 gap-2 mt-2">
                    {[1, 12, 24, 48].map((hours) => (
                      <button
                        key={hours}
                        type="button"
                        onClick={() => setFormData({ ...formData, deadline_hours: hours })}
                        className={`p-2 rounded border-2 text-sm font-semibold ${
                          formData.deadline_hours === hours ? 'bg-[#D4AF37] text-[#1A1A1A] border-[#D4AF37]' : 'bg-[#2D2D2D] text-white border-[#3D3D3D]'
                        }`}
                      >
                        {hours === 1 ? '1 Hour' : hours === 12 ? '12 Hours' : hours === 24 ? '24 Hours' : '48 Hours'}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Visibility */}
                <div>
                  <Label className="font-semibold text-white">Bet Visibility</Label>
                  <div className="flex gap-2 mt-2">
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, visibility: 'public' })}
                      className={`flex-1 p-3 rounded-lg border-2 font-semibold ${
                        formData.visibility === 'public' ? 'bg-[#28A745] text-white border-[#28A745]' : 'bg-[#2D2D2D] text-white border-[#3D3D3D]'
                      }`}
                      data-testid="visibility-public"
                    >
                      <Globe className="h-5 w-5 inline mr-2" />
                      Public
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, visibility: 'private' })}
                      className={`flex-1 p-3 rounded-lg border-2 font-semibold ${
                        formData.visibility === 'private' ? 'bg-[#D4AF37] text-[#1A1A1A] border-[#D4AF37]' : 'bg-[#2D2D2D] text-white border-[#3D3D3D]'
                      }`}
                      data-testid="visibility-private"
                    >
                      <Lock className="h-5 w-5 inline mr-2" />
                      Private
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.visibility === 'public' ? 'Everyone can see and match this bet' : 'Only people with the link can see this bet'}
                  </p>
                </div>

                {/* Summary */}
                <div className="bg-[#2D2D2D] p-4 rounded-lg border border-[#D4AF37]/30">
                  <h3 className="font-bold mb-3 text-[#D4AF37]">Summary</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-400">Category:</span>
                      <span className="font-semibold capitalize text-white">{formData.category}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Your Stake:</span>
                      <span className="font-semibold text-[#D4AF37]">
                        {formData.stake_type === 'real' ? '$' : ''}{formData.stake_amount}{formData.stake_type === 'qbean' ? ' QBC' : ''}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Your Pick:</span>
                      <span className="font-semibold text-white">{formData.option_a}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Potential Win:</span>
                      <span className="font-semibold text-[#28A745]">
                        {formData.stake_type === 'real' ? '$' : ''}{(formData.stake_amount * 2 * 0.9).toFixed(2)}{formData.stake_type === 'qbean' ? ' QBC' : ''}
                      </span>
                    </div>
                  </div>
                </div>

                <Button 
                  type="submit"
                  className="btn-primary w-full"
                  disabled={loading}
                  data-testid="submit-bet-btn"
                >
                  {loading ? 'Creating...' : 'Create Bet'}
                </Button>

                <p className="text-xs text-gray-500 text-center">
                  Your bet will be reviewed by our moderation team before going live (usually within 1 hour).
                </p>
              </div>
            </form>
          )}
        </div>
        </>
        )}
      </main>

      <Footer />
      <MobileBottomNav />
    </div>
  );
};

export default CreateBet;
